"use client";

import { CMSLink } from "@/Link";
import type { ResolvedCMSLink } from "@/blocks/resolve";
import { cn } from "@/lib/utils";

interface CTAProps {
  sectionId?: string;
  title?: string;
  titleTag?: "h1" | "h2" | "h3";
  titleClassName?: string;
  description?: string;
  colorMode?: "light" | "dark" | "midnight";
  size?: "default" | "sm" | "lg" | "xl" | "icon";
  link?: ResolvedCMSLink;
}

export function CTA({
  sectionId,
  title,
  titleTag = "h2",
  titleClassName = "text-xl",
  description,
  colorMode,
  size,
  link,
}: CTAProps) {
  const resolvedColorMode = colorMode ?? "light";
  const isDarkMode = resolvedColorMode === "dark";
  const isMidnightMode = resolvedColorMode === "midnight";
  const isDarkTone = isDarkMode || isMidnightMode;
  const TitleTag = titleTag;

  return (
    <section id={sectionId} className="bg-background section-padding-y">
      <div className="container-padding-x container mx-auto">
        <div
          className={cn(
            "flex w-full flex-col items-center gap-6 rounded-sm p-6 md:p-8",
            isMidnightMode
              ? "bg-[#0D0F33]"
              : isDarkMode
                ? "bg-foreground"
                : "bg-[#f1f0ee]",
          )}
        >
          <div className="flex flex-col gap-2 text-center">
            <TitleTag
              className={cn(
                titleClassName,
                isDarkTone ? "text-white" : "text-foreground",
              )}
            >
              {title}
            </TitleTag>
            <p
              className={cn(
                "text-base",
                isDarkTone ? "text-white/72" : "text-muted-foreground",
              )}
            >
              {description}
            </p>
          </div>
          {link ? (
            <div className="flex flex-wrap items-center justify-center gap-3">
              <CMSLink
                {...link}
                appearance={link.appearance ?? "default"}
                colorMode={isDarkTone ? "dark" : "light"}
                size={size}
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
